import type { CorpoErro } from './tipos.ts';

const BASE = '/api/v1';

/**
 * Token da sessão atual.
 *
 * Fica em módulo, e não em contexto do React, porque quem precisa dele é o
 * `fetch` — passá-lo por props ou hook até cada chamada obrigaria toda função
 * de API a receber um parâmetro que não tem nada a ver com o que ela faz. O
 * provedor de sessão é o único que escreve aqui.
 */
let tokenAtual: string | null = null;

let aoPerderSessaoCb: (() => void) | null = null;

export function definirToken(token: string | null): void {
  tokenAtual = token;
}

/**
 * Registra o que fazer quando o servidor recusar a sessão. É como um token
 * vencido em outra aba, ou um acesso inativado pelo administrador, vira uma
 * volta para a tela de login em vez de uma tela cheia de erro.
 */
export function aoPerderSessao(callback: () => void): void {
  aoPerderSessaoCb = callback;
}

/**
 * Erro de API já traduzido. Carrega o código do servidor para a tela poder
 * reagir ao caso específico em vez de comparar mensagens.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly detalhes: Record<string, unknown> | undefined;

  constructor(
    status: number,
    codigo: string,
    mensagem: string,
    detalhes?: Record<string, unknown>,
  ) {
    super(mensagem);
    this.name = 'ApiError';
    this.status = status;
    this.codigo = codigo;
    this.detalhes = detalhes;
  }
}

interface OpcoesRequisicao {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  /**
   * Corpo que não é JSON — hoje, o `FormData` do envio de XMLs. Vai cru, e
   * sem `content-type` declarado: é o navegador que precisa escrever o
   * `boundary` do multipart, e declarar o cabeçalho na mão quebraria o envio.
   */
  corpoBruto?: BodyInit | undefined;
  parametros?: Record<string, string | number | undefined>;
  sinal?: AbortSignal;
  /**
   * Para as chamadas em que 401 é uma resposta esperada, não uma sessão
   * perdida: login com senha errada e troca de senha com a atual errada. Sem
   * isto, errar a senha atual expulsaria o usuário do sistema.
   */
  trataProprio401?: boolean;
}

export async function requisitar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const url = new URL(`${BASE}${caminho}`, window.location.origin);

  for (const [chave, valor] of Object.entries(opcoes.parametros ?? {})) {
    if (valor !== undefined && valor !== '') {
      url.searchParams.set(chave, String(valor));
    }
  }

  const cabecalhos: Record<string, string> = {};
  if (opcoes.corpo !== undefined) {
    cabecalhos['content-type'] = 'application/json';
  }
  if (tokenAtual !== null) {
    cabecalhos.authorization = `Bearer ${tokenAtual}`;
  }

  const resposta = await fetch(url, {
    method: opcoes.metodo ?? 'GET',
    headers: cabecalhos,
    body: opcoes.corpoBruto ?? (opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo)),
    signal: opcoes.sinal ?? null,
  });

  if (!resposta.ok) {
    const erro = await montarErro(resposta);

    if (erro.status === 401 && opcoes.trataProprio401 !== true) {
      aoPerderSessaoCb?.();
    }

    throw erro;
  }

  if (resposta.status === 204) {
    return undefined as T;
  }

  return (await resposta.json()) as T;
}

async function montarErro(resposta: Response): Promise<ApiError> {
  try {
    const corpo = (await resposta.json()) as Partial<CorpoErro>;
    if (corpo.erro !== undefined) {
      return new ApiError(
        resposta.status,
        corpo.erro.codigo,
        corpo.erro.mensagem,
        corpo.erro.detalhes,
      );
    }
  } catch {
    // Resposta sem JSON: cai no erro genérico abaixo.
  }

  return new ApiError(
    resposta.status,
    'ERRO_HTTP',
    `Falha na requisição (HTTP ${resposta.status}).`,
  );
}

export interface ArquivoBaixado {
  readonly nomeSugerido: string;
  readonly conteudo: Blob;
  /** Cabeçalhos próprios da rota, quando ela mandar algum. */
  readonly cabecalhos: Headers;
}

/**
 * Baixa um arquivo da API.
 *
 * Um `<a href>` simples não serve: o navegador não manda o cabeçalho
 * `Authorization` numa navegação, então o download voltaria 401. O caminho é
 * buscar com o token, virar `Blob` e disparar o clique num link temporário.
 *
 * Aceita `POST` porque nem todo download é leitura: gerar o instalador do
 * agente emite uma credencial nova e invalida a anterior, e isso não pode ir
 * num GET — qualquer pré-carregamento do navegador derrubaria a instalação de
 * um cliente sem ninguém ter clicado em nada.
 */
export async function baixarArquivo(
  caminho: string,
  parametros: Record<string, string | number | undefined> = {},
  opcoes: {
    metodo?: 'GET' | 'POST';
    corpo?: unknown;
    /**
     * Para os downloads em que 401 não é sessão perdida: o portal do contador,
     * onde 401 significa link vencido ou revogado e quem está ali nem sessão
     * tem. Sem isto, abrir um link vencido mandaria o contador para a tela de
     * login do escritório.
     */
    trataProprio401?: boolean;
  } = {},
): Promise<ArquivoBaixado> {
  const url = new URL(`${BASE}${caminho}`, window.location.origin);
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor !== undefined && valor !== '') url.searchParams.set(chave, String(valor));
  }

  const cabecalhos: Record<string, string> = {};
  if (tokenAtual !== null) cabecalhos.authorization = `Bearer ${tokenAtual}`;
  if (opcoes.corpo !== undefined) cabecalhos['content-type'] = 'application/json';

  const resposta = await fetch(url, {
    method: opcoes.metodo ?? 'GET',
    headers: cabecalhos,
    body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
  });

  if (!resposta.ok) {
    const erro = await montarErro(resposta);
    if (erro.status === 401 && opcoes.trataProprio401 !== true) aoPerderSessaoCb?.();
    throw erro;
  }

  return {
    nomeSugerido: nomeDoCabecalho(resposta.headers) ?? 'download',
    conteudo: await resposta.blob(),
    cabecalhos: resposta.headers,
  };
}

/**
 * Entrega o arquivo ao navegador.
 *
 * O `revokeObjectURL` não é higiene opcional: sem ele, o blob fica preso na
 * memória da aba até a página ser recarregada, e um contador que baixa trinta
 * competências numa sessão paga isso.
 */
export function salvarArquivo(arquivo: ArquivoBaixado, nome = arquivo.nomeSugerido): void {
  const url = URL.createObjectURL(arquivo.conteudo);

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Pequeno atraso: revogar no mesmo instante do clique cancela o download
    // em alguns navegadores.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

function nomeDoCabecalho(cabecalhos: Headers): string | null {
  const disposicao = cabecalhos.get('content-disposition');
  if (disposicao === null) return null;

  const encontrado = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposicao);
  return encontrado?.[1] === undefined ? null : decodeURIComponent(encontrado[1]);
}
